import React, { useMemo } from 'react';
import { Alert, Card, Select, Table } from 'antd';
import PropTypes from 'prop-types';

import { DollarCircleOutlined, TeamOutlined, TrophyFilled } from '@ant-design/icons';
import dayjs from '@tt/dates/dayjs';

import './styles.scss';

const HEADER_STYLE = { fontWeight: 700 };
const CELL_STYLE = { paddingInline: 12, paddingBlock: 10 };
const JOURNAL_BASE_URL = 'https://journal.tradingterminal.com';
const COL_WIDTH = {
    rank: 58,
    trader: 162,
    accountId: 128,
    profit: 196,
    paidDate: 118
};
const CURRENCY_NO_DECIMALS = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
});

const normalizeNumber = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
};

const truncateDecimals = (value) => Math.trunc(normalizeNumber(value));

const formatCurrencyNoDecimals = (value) => CURRENCY_NO_DECIMALS.format(truncateDecimals(value));

const getMedalToneClass = (rank) => {
    if (rank === 1) return 'lb-tone-gold';
    if (rank === 2) return 'lb-tone-silver';
    if (rank === 3) return 'lb-tone-bronze';
    return '';
};

const getProgressPercent = (pnl = 0, maxPnl = 1) => {
    if (maxPnl <= 0) return 0;
    return Math.max(0, Math.min(100, (pnl / maxPnl) * 100));
};

const getHeroTitle = (period = 'all_time', periodLabel = 'All Time') => {
    const normalizedPeriod = String(period || '')
        .trim()
        .toLowerCase();
    if (normalizedPeriod === 'current_traders') return 'Top Current Trader';
    if (normalizedPeriod === 'last_30_days') return 'Top Trader This Month';
    if (normalizedPeriod === 'all_time') return 'Top Trader All Time';

    const normalizedLabel = String(periodLabel || '')
        .trim()
        .toLowerCase();
    if (normalizedLabel.includes('current')) return 'Top Current Trader';
    if (normalizedLabel.includes('30') || normalizedLabel.includes('month'))
        return 'Top Trader This Month';
    if (normalizedLabel.includes('all')) return 'Top Trader All Time';
    return 'Top Trader';
};

export default function FundedAccountsLeaderboard({
    rows = [],
    isLoading = false,
    error = null,
    topLeaderboard = null,
    fixedStats = null,
    period = 'all_time',
    onPeriodChange = () => {},
    page = 1,
    pageSize = 25,
    total = 0,
    onTableChange = () => {},
    sortBy = 'paidDate',
    sortOrder = 'desc'
}) {
    const topRows = useMemo(() => (topLeaderboard?.rows || []).slice(0, 5), [topLeaderboard?.rows]);

    const topTableMaxPnl = useMemo(() => {
        const computedMax = topRows.reduce((maxValue, row) => {
            const pnl = typeof row?.pnl === 'number' ? row.pnl : 0;
            return Math.max(maxValue, pnl);
        }, 0);
        return Math.max(1, computedMax);
    }, [topRows]);

    const topTableData = useMemo(
        () =>
            topRows.map((row, index) => ({
                ...row,
                key: row.accountUniqueId || `top-${index}`,
                rank: row.rank || index + 1,
                progressPercent: getProgressPercent(row.pnl, topTableMaxPnl)
            })),
        [topRows, topTableMaxPnl]
    );

    const tableData = useMemo(
        () =>
            rows.map((row, index) => ({
                ...row,
                key: row.accountUniqueId || `row-${index}`,
                rank: row.rank || (page - 1) * pageSize + index + 1
            })),
        [rows, page, pageSize]
    );

    let pnlSortOrder = null;
    if (sortBy === 'pnl') {
        pnlSortOrder = sortOrder === 'asc' ? 'ascend' : 'descend';
    }

    let paidDateSortOrder = null;
    if (sortBy === 'paidDate') {
        paidDateSortOrder = sortOrder === 'asc' ? 'ascend' : 'descend';
    }

    const periodOptions = [
        { label: 'All Time', value: 'all_time' },
        { label: 'Last 30 Days', value: 'last_30_days' },
        { label: 'Current Traders', value: 'current_traders' }
    ];

    const topColumns = useMemo(
        () => [
            {
                title: 'Rank',
                dataIndex: 'rank',
                key: 'rank',
                width: COL_WIDTH.rank,
                onHeaderCell: () => ({ style: { ...HEADER_STYLE, ...CELL_STYLE } }),
                onCell: () => ({ style: CELL_STYLE }),
                render: (rank) => {
                    const toneClass = getMedalToneClass(rank);
                    if (toneClass) {
                        return (
                            <span className={`lb-rank-medal ${toneClass}`}>
                                <TrophyFilled />
                            </span>
                        );
                    }

                    return <span className="lb-rank-number">{rank}</span>;
                }
            },
            {
                title: 'Trader',
                dataIndex: 'traderName',
                key: 'traderName',
                width: COL_WIDTH.trader,
                ellipsis: true,
                onHeaderCell: () => ({ style: { ...HEADER_STYLE, ...CELL_STYLE } }),
                onCell: () => ({ style: CELL_STYLE }),
                render: (value, record) => {
                    const displayValue = value || 'Unknown';
                    const traderLink = record.accountUniqueId
                        ? `${JOURNAL_BASE_URL}/${encodeURIComponent(record.accountUniqueId)}`
                        : '';
                    return (
                        <div className="lb-trader-cell" title={displayValue}>
                            {traderLink ? (
                                <a
                                    className="lb-trader-main lb-trader-name-link"
                                    href={traderLink}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {displayValue}
                                </a>
                            ) : (
                                <span className="lb-trader-main">{displayValue}</span>
                            )}
                        </div>
                    );
                }
            },
            {
                title: 'Account ID',
                dataIndex: 'accountUniqueId',
                key: 'accountUniqueId',
                responsive: ['lg'],
                width: COL_WIDTH.accountId,
                ellipsis: true,
                onHeaderCell: () => ({ style: { ...HEADER_STYLE, ...CELL_STYLE } }),
                onCell: () => ({ style: CELL_STYLE }),
                render: (value) => {
                    const accountIdValue = String(value || '').trim();
                    if (!accountIdValue) return <span className="lb-account-id-empty">-</span>;
                    const traderLink = `${JOURNAL_BASE_URL}/${encodeURIComponent(accountIdValue)}`;
                    return (
                        <a
                            className="lb-account-id-link"
                            href={traderLink}
                            target="_blank"
                            rel="noreferrer"
                            title={accountIdValue}
                        >
                            {accountIdValue}
                        </a>
                    );
                }
            },
            {
                title: 'Profit',
                dataIndex: 'pnl',
                key: 'pnl',
                width: COL_WIDTH.profit,
                onHeaderCell: () => ({ style: { ...HEADER_STYLE, ...CELL_STYLE } }),
                onCell: () => ({ style: CELL_STYLE }),
                render: (value, record) => {
                    const pnl = typeof value === 'number' ? value : 0;
                    const isPositive = pnl >= 0;
                    return (
                        <div className="lb-profit-cell">
                            <span
                                className={`lb-profit-value ${
                                    isPositive ? 'positive' : 'negative'
                                }`}
                            >
                                {isPositive ? '+' : ''}
                                {formatCurrencyNoDecimals(pnl)}
                            </span>
                            <div className="lb-progress-track">
                                <div
                                    className="lb-progress-fill"
                                    style={{ width: `${record.progressPercent || 0}%` }}
                                />
                            </div>
                        </div>
                    );
                }
            },
            {
                title: 'Paid Date',
                dataIndex: 'closedPaidAt',
                key: 'closedPaidAt',
                width: COL_WIDTH.paidDate,
                onHeaderCell: () => ({ style: { ...HEADER_STYLE, ...CELL_STYLE } }),
                onCell: () => ({ style: CELL_STYLE }),
                render: (value) => {
                    if (!value || !dayjs(value).isValid())
                        return <span className="lb-paid-pending">In progress</span>;
                    return (
                        <span className="lb-paid-date">{dayjs(value).format('MMM DD, YYYY')}</span>
                    );
                }
            }
        ],
        []
    );

    const columns = useMemo(
        () => [
            {
                title: 'Rank',
                dataIndex: 'rank',
                key: 'rank',
                width: COL_WIDTH.rank,
                onHeaderCell: () => ({ style: { ...HEADER_STYLE, ...CELL_STYLE } }),
                onCell: () => ({ style: CELL_STYLE }),
                render: (rank) => <span className="lb-rank-number">{rank}</span>
            },
            {
                title: 'Trader',
                dataIndex: 'traderName',
                key: 'traderName',
                width: COL_WIDTH.trader,
                ellipsis: true,
                onHeaderCell: () => ({ style: { ...HEADER_STYLE, ...CELL_STYLE } }),
                onCell: () => ({ style: CELL_STYLE }),
                render: (value, record) => {
                    const displayValue = value || 'Unknown';
                    const traderLink = record.accountUniqueId
                        ? `${JOURNAL_BASE_URL}/${encodeURIComponent(record.accountUniqueId)}`
                        : '';
                    return (
                        <div className="lb-trader-cell lb-trader-cell-compact" title={displayValue}>
                            {traderLink ? (
                                <a
                                    className="lb-trader-main lb-trader-name-link"
                                    href={traderLink}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {displayValue}
                                </a>
                            ) : (
                                <span className="lb-trader-main">{displayValue}</span>
                            )}
                        </div>
                    );
                }
            },
            {
                title: 'Account ID',
                dataIndex: 'accountUniqueId',
                key: 'accountUniqueId',
                responsive: ['lg'],
                width: COL_WIDTH.accountId,
                ellipsis: true,
                onHeaderCell: () => ({ style: { ...HEADER_STYLE, ...CELL_STYLE } }),
                onCell: () => ({ style: CELL_STYLE }),
                render: (value) => {
                    const accountIdValue = String(value || '').trim();
                    if (!accountIdValue) return <span className="lb-account-id-empty">-</span>;
                    const traderLink = `${JOURNAL_BASE_URL}/${encodeURIComponent(accountIdValue)}`;
                    return (
                        <a
                            className="lb-account-id-link"
                            href={traderLink}
                            target="_blank"
                            rel="noreferrer"
                            title={accountIdValue}
                        >
                            {accountIdValue}
                        </a>
                    );
                }
            },
            {
                title: 'Profit',
                dataIndex: 'pnl',
                key: 'pnl',
                width: COL_WIDTH.profit,
                sorter: true,
                sortOrder: pnlSortOrder,
                onHeaderCell: () => ({ style: { ...HEADER_STYLE, ...CELL_STYLE } }),
                onCell: () => ({ style: CELL_STYLE }),
                render: (value) => {
                    const pnl = normalizeNumber(value);
                    const isPositive = pnl >= 0;
                    return (
                        <div className="lb-profit-cell lb-profit-cell-plain">
                            <span
                                className={`lb-profit-value ${
                                    isPositive ? 'positive' : 'negative'
                                }`}
                            >
                                {isPositive ? '+' : ''}
                                {formatCurrencyNoDecimals(pnl)}
                            </span>
                        </div>
                    );
                }
            },
            {
                title: 'Paid Date',
                dataIndex: 'closedPaidAt',
                key: 'closedPaidAt',
                width: COL_WIDTH.paidDate,
                sorter: true,
                sortOrder: paidDateSortOrder,
                onHeaderCell: () => ({ style: { ...HEADER_STYLE, ...CELL_STYLE } }),
                onCell: () => ({ style: CELL_STYLE }),
                render: (value) => {
                    if (!value || !dayjs(value).isValid())
                        return <span className="lb-paid-pending">In progress</span>;
                    return (
                        <span className="lb-paid-date">{dayjs(value).format('MMM DD, YYYY')}</span>
                    );
                }
            }
        ],
        [paidDateSortOrder, pnlSortOrder]
    );

    const topTrader = topLeaderboard?.topTrader || null;
    const topTraderName = topTrader?.traderName || 'No qualifying trader';
    const resolvedFixedStats = fixedStats || topLeaderboard?.stats || {};
    const statsPaidAccountsCount =
        typeof resolvedFixedStats?.paidAccountsCount === 'number'
            ? resolvedFixedStats.paidAccountsCount
            : topLeaderboard?.stats?.paidAccountsCount || 0;
    const statsPaidAccountsLabel = new Intl.NumberFormat('en-US').format(statsPaidAccountsCount);
    const statsTotalPayout =
        typeof resolvedFixedStats?.totalPayoutPaid === 'number'
            ? resolvedFixedStats.totalPayoutPaid
            : 0;
    const heroTitle = getHeroTitle(period, topLeaderboard?.periodLabel || 'All Time');

    return (
        <Card className="funded-leaderboard-card" bodyStyle={{ padding: 18 }}>
            {error && (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 14 }}
                    message="Failed to load leaderboard"
                    description={error?.response?.data?.error || 'Please try again later.'}
                />
            )}

            <div className="lb-linked-group">
                <div className="lb-hero">
                    <div className="lb-hero-art" aria-hidden="true">
                        <div className="lb-podium lb-podium-left" />
                        <div className="lb-podium lb-podium-center" />
                        <div className="lb-podium lb-podium-right" />
                        <TrophyFilled className="lb-hero-art-trophy" />
                    </div>

                    <div className="lb-hero-header">
                        <div className="lb-hero-left">
                            <span className="lb-cup-icon">
                                <TrophyFilled />
                            </span>
                            <span className="lb-hero-title">{heroTitle}</span>
                        </div>
                        <Select
                            className="lb-period-select"
                            value={period}
                            options={periodOptions}
                            onChange={onPeriodChange}
                            size="middle"
                        />
                    </div>

                    <div className="lb-hero-main">
                        <div className="lb-hero-meta">
                            <div className="lb-hero-trader-row">
                                <div className="lb-hero-trader">{topTraderName}</div>
                                <div className="lb-hero-profit">
                                    {topTrader ? '+' : ''}
                                    {formatCurrencyNoDecimals(topTrader?.pnl || 0)}
                                    <span className="lb-hero-profit-label"> Profit</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="lb-hero-stats">
                        <div className="lb-stat-item">
                            <TeamOutlined className="lb-stat-icon" />
                            <span className="lb-stat-text">
                                Total Paid Accounts: {statsPaidAccountsLabel}
                            </span>
                        </div>
                        <div className="lb-stat-item">
                            <DollarCircleOutlined className="lb-stat-icon" />
                            <span className="lb-stat-text">
                                Total Payout: {formatCurrencyNoDecimals(statsTotalPayout)}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="lb-table-shell lb-top-shell">
                    <Table
                        columns={topColumns}
                        dataSource={topTableData}
                        loading={isLoading}
                        scroll={{ x: 'max-content' }}
                        pagination={false}
                        tableLayout="fixed"
                        className="lb-table lb-top-table"
                        locale={{
                            emptyText: isLoading
                                ? 'Loading top traders...'
                                : 'No paid traders found for this range.'
                        }}
                    />
                </div>
            </div>

            <div className="lb-table-shell">
                <div className="lb-table-heading">Latest Paid Funded Accounts</div>
                <Table
                    columns={columns}
                    dataSource={tableData}
                    loading={isLoading}
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        current: page,
                        pageSize,
                        total,
                        showSizeChanger: false,
                        size: 'small'
                    }}
                    onChange={onTableChange}
                    tableLayout="fixed"
                    className="lb-table lb-main-table"
                    locale={{
                        emptyText: isLoading
                            ? 'Loading leaderboard...'
                            : 'No paid funded accounts found.'
                    }}
                />
            </div>
        </Card>
    );
}

FundedAccountsLeaderboard.propTypes = {
    rows: PropTypes.arrayOf(
        PropTypes.shape({
            traderName: PropTypes.string,
            accountUniqueId: PropTypes.string,
            pnl: PropTypes.number,
            closedPaidAt: PropTypes.string
        })
    ),
    topLeaderboard: PropTypes.shape({
        period: PropTypes.oneOf(['all_time', 'last_30_days', 'current_traders']),
        periodLabel: PropTypes.string,
        maxPnl: PropTypes.number,
        stats: PropTypes.shape({
            paidAccountsCount: PropTypes.number,
            totalPayoutPaid: PropTypes.number
        }),
        topTrader: PropTypes.shape({
            accountUniqueId: PropTypes.string,
            traderName: PropTypes.string,
            pnl: PropTypes.number
        }),
        rows: PropTypes.arrayOf(
            PropTypes.shape({
                rank: PropTypes.number,
                accountUniqueId: PropTypes.string,
                traderName: PropTypes.string,
                pnl: PropTypes.number,
                closedPaidAt: PropTypes.string
            })
        )
    }),
    fixedStats: PropTypes.shape({
        paidAccountsCount: PropTypes.number,
        totalPayoutPaid: PropTypes.number
    }),
    period: PropTypes.oneOf(['all_time', 'last_30_days', 'current_traders']),
    onPeriodChange: PropTypes.func,
    isLoading: PropTypes.bool,
    error: PropTypes.shape({
        response: PropTypes.shape({
            data: PropTypes.shape({
                error: PropTypes.string
            })
        })
    }),
    page: PropTypes.number,
    pageSize: PropTypes.number,
    total: PropTypes.number,
    onTableChange: PropTypes.func,
    sortBy: PropTypes.oneOf(['paidDate', 'pnl']),
    sortOrder: PropTypes.oneOf(['asc', 'desc'])
};
